import {useEffect, useRef, useState} from 'react';
import {
  get,
  filter,
  isNull,
  omitBy,
  isEmpty,
  orderBy,
  isString,
  isArray,
  toLower,
  kebabCase,
  capitalize,
  find,
} from 'lodash';
import Modal from 'react-modal';
import RecipeEditor from './RecipeEditor';
import {shippingSettings} from '../../../internals/managers/ShopSettingsManager';
import {nanoid} from 'nanoid';
import {Formik, Field} from 'formik';
import Select from 'react-select';
import Switch from 'react-switch';
import CreatableSelect from 'react-select/creatable';
import {ProductTag, Products} from '../Types';
import {validateImageFile} from '../../../internals/managers/ImageManager';
import PriceForm from './PriceForm';
import {buildPriceVariants} from '../priceVariants';
import VariantStockTable from './VariantStockTable';
import StockAdjustModal from '../../stockLevels/views/StockAdjustModal';
import {
  afterAdjust,
  canTrackByVariant,
  cleanVariantConfig,
  variantRows,
  variantsOf,
  type VariantRow,
} from '../variantStock';
import Dropzone from 'react-dropzone';
import {Schema} from '../Validator';
import {connect} from 'react-redux';
import OptionForm from './OptionForm';
import ModifierForm from './ModifierForm';
import {AiOutlineClose} from 'react-icons/ai';
import {HiOutlineArrowLeft} from 'react-icons/hi';
import {
  HiOutlineShoppingBag,
  HiCheck,
  HiChevronRight,
  HiOutlineExclamationTriangle,
  HiOutlinePhoto,
  HiOutlineArrowUpTray,
  HiPlus,
} from 'react-icons/hi2';
import S3Image from '../../../components/S3Image';
import {
  mapBrand,
  mapCategory,
  mapWarranty,
  productOptions,
} from '../../../internals/managers/ProductManager';
import AddBrand from '../../../components/AddBrand';
import {Actions} from '../../../internals/app/Actions';
import AddCategory from '../../../components/AddCategory';
import DeleteModal from '../../../components/Modal/DeleteModal';
import AddWarranty from '../../../components/Modal/AddWarranty';
import FormInput from '../../../components/inputs/FormInput';
import CustomSelect from '../../../components/inputs/CustomSelect';
import MDXEditorInput from '../../../components/inputs/MDXEditorInput';
import {MDXEditorMethods} from '@mdxeditor/editor';
import {formatPrice} from '../../../internals/managers/PaymentManager';
import defaultImage from '../../../assets/images/default-store.jpeg';
import ImageRemoveIcon from '../../../assets/svgs/imageRemoveIcon.svg';
import ProductRepository from '../../../internals/repository/ProductRepository';
import {portalMenu} from '../../../components/ui';

const customStyles: any = {
  overlay: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 40,
    display: 'flex',
    position: 'fixed',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(19, 19, 24, 0.55)',
  },
  content: {
    inset: 'auto',
    padding: 0,
    border: 'none',
    background: 'none',
    overflow: 'visible',
    position: 'relative',
    WebkitOverflowScrolling: 'touch',
  },
};

const selectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: '42px',
    boxShadow: 'none',
    borderRadius: '10px',
    borderColor: 'var(--border)',
    '&:hover': {borderColor: 'var(--border2)'},
  }),
  indicatorSeparator: () => ({display: 'none'}),
};

// ── Wizard steps (design: 6-step add/edit product flow) ──────────────────────
const STEPS = [
  {n: 1, title: 'Essentials', sub: 'Name, price'},
  {n: 2, title: 'Photos', sub: 'Product & menu images'},
  {n: 3, title: 'Organisation', sub: 'Stock, category, tags'},
  {n: 4, title: 'Options', sub: 'Variants, modifiers, warranty'},
  {n: 5, title: 'Behaviour', sub: 'Routing & description'},
  {n: 6, title: 'Review', sub: 'Check, then publish'},
];

type ProductProps = {
  tags: Array<ProductTag>;
  brands: any;
  product?: any;
  imageId: string;
  menuImage: string;
  isOpen: boolean;
  categories: any;
  warranties: any;
  isEdit?: boolean;
  currentUser: any;
  selectedShop: any;
  loadingAction: any;
  closeModal: () => void;
  removeImage: () => void;
  setImage: (params: any) => void;
  uploadImage: (params: any, type?: string) => void;
  updateProduct: (params: any) => void;
  createProduct: (params: any) => void;
  createProductTag: (params: any) => void;
  fetchProductOptionSets: (params: any) => void;
  deleteProduct: (productId: string, shopId: string) => void;
  fetchProductTags: (shopId: string) => void;
  /**
   * Starting values for a NEW product, e.g. the name typed into another
   * screen's picker. Ignored when editing.
   */
  newDefaults?: Partial<typeof Initials>;
  /** Called with the new product's id once a create has been sent. */
  onCreated?: (created: {pid: string; productName: string}) => void;
  /** The ingredient master, for the recipe editor. */
  ingredients: any;
  fetchIngredients: (shopId: string, includeInactive?: boolean) => void;
};

const Initials = {
  brand: '',
  warranty: {},
  category: '',
  modifiers: [],
  itemPrice: '',
  shippingFee: '',
  productName: '',
  description: '',
  priceVariants: [],
  productOptions: [],
  stockMode: 'none',
  parLevel: '',
  returnToStockOnRefund: false,
  recipe: [],
  trackByVariant: false,
  variantConfig: {},
};

/** Stock tracking modes. Exclusive: a product is exactly one of these. */
const STOCK_MODES = [
  {value: 'none', label: 'Not tracked', hint: 'No stock shown anywhere for this item'},
  {value: 'unit', label: 'Count units', hint: 'A running count that carries across days'},
  {value: 'prep', label: 'Daily prep', hint: 'Counted each morning; leftovers written off at close'},
  {
    value: 'recipe',
    label: 'Recipe',
    hint: 'Made from ingredients; the count is how many portions they allow',
  },
];

const STOCK_EVENT_LABEL: Record<string, string> = {
  sale: 'Sale',
  refund: 'Refund',
  waste: 'Waste',
  purchase: 'Received',
  prep_set: 'Prep count',
  count_adjust: 'Counted',
  override_sale: 'Override',
};

/**
 * Attributes the product form must hand back untouched because the update is
 * a whole-item replace. Only keys the record actually has are returned, so a
 * product that has never been tracked does not gain empty stock fields.
 */
function preservedProductFields(product: any) {
  const carried: any = {};
  [
    'stockOnHand',
    'stockBaselineAt',
    'soldOut',
    'stockUpdatedAt',
    'availability',
    'uberEatsSync',
    'reportTypeId',
  ].forEach((key) => {
    if (product && product[key] !== undefined) carried[key] = product[key];
  });
  return carried;
}

const AddProduct = (props: ProductProps) => {
  const {
    tags,
    isOpen,
    brands,
    product,
    imageId,
    closeModal,
    categories,
    warranties,
    currentUser,
    removeImage,
    uploadImage,
    selectedShop,
    updateProduct,
    createProduct,
    deleteProduct,
    loadingAction,
    createProductTag,
    fetchProductTags,
    fetchProductOptionSets,
    ingredients,
    fetchIngredients,
    newDefaults,
    onCreated,
  } = props;
  // The recipe editor needs the ingredient master. Loaded when the form
  // opens rather than on mount: this component is rendered by every product
  // row and most of them never open.
  useEffect(() => {
    if (isOpen && selectedShop?.shopId) fetchIngredients(selectedShop.shopId);
  }, [isOpen, selectedShop, fetchIngredients]);

  /** The variant being counted or received from the form, if any. */
  const [variantAdjust, setVariantAdjust] = useState<{row: VariantRow; mode: 'count' | 'receive'} | null>(null);
  /** Figures moved from this form, shown until the product is reloaded. */
  const [variantOnHand, setVariantOnHand] = useState<Record<string, number>>({});
  const [variantNotice, setVariantNotice] = useState('');
  useEffect(() => {
    setVariantOnHand({});
    setVariantNotice('');
  }, [product?.pid]);

  // Product shipping is a shop setting; the per-product fee only appears
  // when it is on.
  const shipping = shippingSettings(selectedShop);

  const [menuImage, setMenuImage] = useState<any>();
  const [isUploading, setUploading] = useState<boolean>(false);
  const [productImages, setProductImages] = useState<Array<any>>([]);

  const [step, setStep] = useState<number>(1);
  // Highest step the user has reached — editing starts fully "visited" (6)
  const [visitedMax, setVisitedMax] = useState<number>(1);
  const [slugEditing, setSlugEditing] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editModifier, setEditModifier] = useState<any>({});
  const [isOpenBrand, setIsOpenBrand] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<any>({});
  const [isOpenDelete, setIsOpenDelete] = useState<boolean>(false);
  const [selectedWarranty, setSelectedWarranty] = useState<any>({});
  const [productInitial, setProductInitial] = useState<any>(Initials);
  const [isOpenCategory, setIsOpenCategory] = useState<boolean>(false);
  const [validationSchema, setValidationSchema] = useState<any>(Schema);
  const [openPriceVariant, setOpenPriceVariant] = useState<boolean>(false);
  const [openProductOption, setOpenProductOption] = useState<boolean>(false);
  const [openProductModifier, setOpenProductModifier] =
    useState<boolean>(false);
  const [openWarrantyModal, setOpenWarrantyModal] = useState<boolean>(false);
  const mdxRef = useRef<MDXEditorMethods>(null);
  const optionsInitial = [
    {
      id: nanoid(),
      optionUnit: '',
      optionName: '',
      optionPrice: 0,
    },
  ];

  const fetctOptionSet = async () => {
    const shopId = selectedShop?.shopId;
    if (shopId) {
      fetchProductOptionSets(shopId);
    }
  };

  useEffect(() => {
    if (selectedShop?.shopId) {
      fetchProductTags(selectedShop.shopId);
    }
  }, [selectedShop]);

  useEffect(() => {
    if (isOpen) {
      fetctOptionSet();
      setStep(1);
      setSlugEditing(false);
      // Editing an existing product: all steps count as visited so its data
      // pre-validates the rail; a new product accumulates checks step by step.
      setVisitedMax(isEmpty(product) ? 1 : 6);
    }
  }, [isOpen]);

  useEffect(() => {
    const isValid = !isEmpty(product);
    setIsEdit(isValid);
    // Covers the product prop arriving after the modal is already open
    setVisitedMax(isValid ? 6 : 1);
    if (isValid) {
      let initial = {
        brand: product.brandId,
        category: product.categoryId,
        itemPrice: product.itemPrice,
        shippingFee:
          product.shippingFee === null || product.shippingFee === undefined
            ? ''
            : `${product.shippingFee}`,
        actualPrice: product.actualPrice,
        sendToBar: product.sendToBar,
        showMenu: product.showMenu,
        productName: product.productName,
        description: product.description,
        sendToKitchen: product.sendToKitchen,
        slug: product.slug,
        warranty: get(product, 'warranty', {}),
        modifiers: get(product, 'modifiers', []),
        priceVariants: get(product, 'priceVariants', []),
        productOptions: get(product, 'productOptions', []),
        tags: get(product, 'tags', []).reduce((acc: Array<any>, tag: string) => {
          const found = tags.find(({tagName}) => tagName === tag);
          if (found) {
            acc.push({
              value: found.tagId,
              label: found.tagName,
              shopId: found.shopId,
            });
          }
          return acc;
        }, []),
        // Carried, never defaulted. The storefronts still read it: missing
        // means "unknown" and the item stays listed, 0 hides it. PUT /product
        // replaces the whole item, so a default here would write a 0 onto
        // every product that never had a count.
        stockCount: get(product, 'stockCount'),
        stockMode: get(product, 'stockMode', 'none'),
        recipe: get(product, 'recipe') || [],
        parLevel: get(product, 'parLevel', null) ?? '',
        returnToStockOnRefund: Boolean(get(product, 'returnToStockOnRefund', false)),
        trackByVariant: get(product, 'trackByVariant') === true,
        variantConfig: get(product, 'variantConfig') || {},
      };
      if (!isEmpty(product?.productOptions)) {
        const updatedSchema = Schema.omit(['itemPrice']);
        setValidationSchema(updatedSchema);
      }
      setProductInitial({...initial});
      setSelectedProduct(product);
      setSelectedWarranty(get(product, 'warranty', {}));
      setMenuImage(product.menuImage);
      setProductImages(product.productImages || []);
    } else {
      setProductInitial({...Initials, ...newDefaults});
    }
    // newDefaults is read when the form opens; a new object on each render
    // of the parent must not reset what the user has typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product]);

  useEffect(() => {
    return () => {
      setOpenPriceVariant(false);
      setOpenProductOption(false);
      setProductInitial({...Initials});
    };
  }, []);

  const formData = (values: any) => {
    const params: any = {
      brandId: values.brand,
      sendToBar: values.sendToBar,
      showMenu: values.showMenu,
      categoryId: values.category,
      description: values.description,
      productName: values.productName,
      searchName: toLower(`${values.productName}`),
      sendToKitchen: values.sendToKitchen,
      stockCount: values.stockCount,
      /**
       * Shipping fee for an online store. ALWAYS written, even when the
       * shop has shipping switched off and the field is hidden: this API
       * replaces the whole item, so omitting it would silently drop a fee
       * the shop had already set.
       *
       * Blank is null, not 0 — null means "use the shop's base fee", and 0
       * means "this one ships free". The two must stay distinguishable.
       */
      shippingFee:
        `${values.shippingFee ?? ''}`.trim() === ''
          ? null
          : Number(values.shippingFee),
      // Stock tracking CONFIG. The derived state that sits beside it on
      // ProductTable (stockOnHand / soldOut / …) is never sent from here —
      // it is carried over in onUpdateProduct, because this API replaces the
      // whole item rather than merging.
      stockMode: values.stockMode || 'none',
      parLevel:
        // Par is units for a unit product and PORTIONS for a recipe dish.
        (values.stockMode === 'unit' || values.stockMode === 'recipe') &&
        `${values.parLevel ?? ''}`.trim() !== ''
          ? Number(values.parLevel)
          : null,
      // Cleared when the mode moves away from recipe — otherwise a POS would
      // keep fanning out a recipe for a dish that no longer has one.
      recipe: values.stockMode === 'recipe' ? values.recipe ?? [] : null,
      returnToStockOnRefund: Boolean(values.returnToStockOnRefund),
      // Per-variant stock config (GRN/variants spec 5.4). Only meaningful for
      // a unit-mode product the cloud has derived variants for; anything else
      // switches it off. `variants` and `variantStock` are never sent — the
      // cloud owns both.
      trackByVariant:
        canTrackByVariant(values.stockMode, product) && values.trackByVariant === true,
      variantConfig: canTrackByVariant(values.stockMode, product)
        ? cleanVariantConfig(values.variantConfig, variantsOf(product))
        : null,
    };
    if (isArray(values?.tags)) {
      params.tags = values.tags.map(({value, label, shopId}: any) => label);
    }
    if (values.slug) {
      params.slug = values.slug;
    }
    if (!isEmpty(values?.productOptions)) {
      params.priceVariants = values.priceVariants.map((priceVariant: any) => {
        const price = parseFloat(priceVariant.price);
        priceVariant.price = price;
        return priceVariant;
      });
      params.productOptions = [...values.productOptions];
    } else {
      params.itemPrice = parseFloat(values.itemPrice);
      if (!isNaN(values.actualPrice))
        params.actualPrice = parseFloat(values.actualPrice);
    }
    if (!isEmpty(values?.modifiers)) {
      const shopId = selectedShop.shopId;
      const modifiers = values.modifiers.map((modifier: any) => {
        const items = modifier.items.map((item: any) => {
          item['itemId'] = nanoid(8);
          item.price = parseFloat(item.price);
          return item;
        });
        const params = {
          shopId,
          items: items,
          id: modifier.id,
          title: modifier.title,
          setName: modifier.setName,
          isMandatory: modifier.isMandatory,
        };
        return params;
      });
      params.modifiers = modifiers;
    }
    if (!isEmpty(selectedWarranty)) {
      params['warranty'] = selectedWarranty;
    }
    return params;
  };

  const saveImage = async () => {
    try {
      setUploading(true);
      // @ts-ignore
      return await ProductRepository.uploadProductImage(menuImage, 'menuImage');
    } catch (e) {
      console.log('Uploading error');
    }
    setUploading(false);
  };

  const getMenuImagePath = async () => {
    if (menuImage?.path) {
      return await saveImage();
    }
    return menuImage;
  };

  const getProductImages = async () => {
    return await Promise.all(
      productImages.map(async (pi) => {
        if (pi.path) {
          try {
            setUploading(true);
            // @ts-ignore
            return await ProductRepository.uploadProductImage(pi, 'menuImage');
          } catch (e) {
            console.log('Uploading error');
          } finally {
            setUploading(false);
          }
        }
        return pi;
      })
    );
  };

  const uploadProductOptionImages = async (productOptions: any) => {
    if (!isArray(productOptions)) {
      return [];
    }
    return await Promise.all(
      productOptions.map(async (item: any) => {
        item.options = await Promise.all(
          item.options?.map(async (option: any) => {
            if (option.optionImage?.path) {
              try {
                setUploading(true);
                const fileName = await ProductRepository.uploadProductImage(
                  option.optionImage,
                  'menuImage'
                );
                option.optionImage = fileName;
              } catch (e) {
                console.log('Uploading error');
              } finally {
                setUploading(false);
              }
            }

            return option;
          })
        );

        return item;
      })
    );
  };

  const onCreateProduct = async (values: any) => {
    const shopId = selectedShop.shopId;
    const formValues = formData(values);
    const menuImagePath = await getMenuImagePath();
    const productImages = await getProductImages();

    formValues.productOptions = await uploadProductOptionImages(
      formValues.productOptions
    );

    const param = {
      shopId,
      ...formValues,
      pid: nanoid(10),
      productImage: imageId,
      menuImage: menuImagePath,
      productImages,
      createdTime: Date.now(),
      updatedTime: Date.now(),
      createdUserId: currentUser.uid,
    };
    createProduct(param);
    onCreated?.({pid: param.pid, productName: param.productName});
    closeModal();
  };

  const onUpdateProduct = async (values: any) => {
    const formValues = formData(values);
    const menuImagePath = await getMenuImagePath();
    const productImages = await getProductImages();

    formValues.productOptions = await uploadProductOptionImages(
      formValues.productOptions
    );

    const param = {
      ...formValues,
      pid: product.pid,
      productImage: imageId,
      menuImage: menuImagePath,
      productImages,
      shopId: product.shopId,
      updatedTime: Date.now(),
      createdTime: product.createdTime,
      createdUserId: product.createdUserId,
      // PUT /product REPLACES the whole ProductTable item — it is a DynamoDB
      // Put, not an update expression. Anything this form does not resend is
      // therefore deleted, which is why createdTime/createdUserId are carried
      // above. These five are carried for the same reason:
      //
      //  - the four derived stock fields are written only by the stock sync
      //    lambda; dropping them would reset a live count to nothing and
      //    un-sold-out an item that is genuinely out.
      //  - `availability` is the manual 86 flag the tills read, and
      //    sellable = availability === 'available' && !soldOut. Losing it
      //    silently puts an item staff pulled back on sale.
      //  - `uberEatsSync: false` is a deliberate opt-out; losing it relists
      //    the product on Uber.
      ...preservedProductFields(product),
    };
    updateProduct(param);
    closeModal();
  };

  const setImageToUpload = async (acceptedFiles: any) => {
    const invalid = validateImageFile(acceptedFiles[0]);
    if (invalid) {
      window.alert(invalid);
      return;
    }
    setMenuImage(acceptedFiles[0]);
  };

  const onAddFile = async (acceptedFiles: any, type?: string) => {
    uploadImage(acceptedFiles[0], type);
  };

  const addProductOption = (status: boolean, values: any) => {
    const options = {
      id: nanoid(),
      setName: '',
      setDisplayName: '',
      options: optionsInitial,
      shopId: selectedShop.shopId,
    };
    if (isEmpty(values?.productOptions)) {
      values['productOptions'].push(options);
    }
    Schema.validate({itemPrice: 1});
    const updatedSchema = Schema.omit(['itemPrice']);
    setValidationSchema(updatedSchema);
    setOpenProductOption(status);
  };

  const addProductModifier = (values: any, errors: any) => {
    let option: any = {
      title: '',
      setName: '',
      id: nanoid(8),
      isMandatory: false,
      items: [
        {
          price: 0,
          itemName: '',
        },
      ],
    };
    values.modifiers.push(option);
    setOpenProductModifier(true);
  };

  const brandList = mapBrand(brands);
  const categoryList = mapCategory(categories);
  const warrantyList = mapWarranty(warranties);

  const onProductDelete = () => {
    deleteProduct(product.pid, product.shopId);
    setIsOpenDelete(false);
    closeModal();
  };

  const toggleBrandModal = () => {
    setIsOpenBrand(!isOpenBrand);
  };

  const toggleCategoryModal = () => {
    setIsOpenCategory(!isOpenCategory);
  };

  const setProductImagesToUploaded = async (acceptedFiles: any) => {
    const errors = acceptedFiles
      .map((f: any) => validateImageFile(f))
      .filter(Boolean);
    if (errors.length > 0) {
      window.alert(errors.join('\n'));
      return;
    }
    setProductImages([...productImages, ...acceptedFiles]);
  };

  const removeProductImage = async (index: number) => {
    productImages.splice(index, 1);
    setProductImages([...productImages]);
  };

  const toggleWarrantyModal = () => {
    setOpenWarrantyModal(!openWarrantyModal);
  };

  const toggleProductOption = () => {
    setOpenProductOption(!openProductOption);
  };

  const togglePriceVariant = () => {
    setOpenPriceVariant(!openPriceVariant);
  };

  const toggleProductModifier = () => {
    setOpenProductModifier(!openProductModifier);
  };

  const onPressSubModalBack = () => {
    if (openProductOption) {
      toggleProductOption();
    } else if (openProductModifier) {
      setEditModifier({});
      toggleProductModifier();
    } else {
      togglePriceVariant();
      toggleProductOption();
    }
  };

  const isSubModal =
    openProductOption || openPriceVariant || openProductModifier;

  const handleSubmitProduct = async (values: Products, {resetForm}: any) => {
    if (openProductModifier) {
      return setOpenProductModifier(false);
    }
    try {
      if (!openProductOption && !openProductModifier) {
        if (!isEdit) {
          await onCreateProduct(values);
        } else {
          await onUpdateProduct(values);
        }
        resetForm();
        setOpenPriceVariant(false);
      }
    } catch (error) {
      console.log('submit product error:', error);
    }
  };

  const addOptionVariants = (values: any, setFieldValue: any) => {
    let oprionsArray: any = [];
    values.productOptions.forEach((productOption: any) => {
      oprionsArray.push([...productOption.options]);
    });
    const priceVariants = buildPriceVariants(
      oprionsArray,
      values.priceVariants,
      Boolean(isEdit),
      () => nanoid(6)
    );
    const orderVariants = orderBy(priceVariants, ['optionUnit'], ['asc']);
    setFieldValue('priceVariants', [...orderVariants]);
    setOpenProductOption(false);
    setOpenPriceVariant(true);
  };

  const onCloseModal = () => {
    setOpenPriceVariant(false);
    setOpenProductOption(false);
    setOpenProductModifier(false);
    Schema.validate({itemPrice: 0});
    const updatedSchema = Schema.required('itemPrice');
    setValidationSchema(updatedSchema);
    setProductInitial({...Initials, productOptions: [], modifiers: []});
    closeModal();
  };

  // ── Step helpers ──────────────────────────────────────────────────────────
  const hasValidPrice = (values: any) =>
    !isEmpty(values.productOptions) ||
    (values.itemPrice !== '' && !isNaN(parseFloat(values.itemPrice)) &&
      parseFloat(values.itemPrice) > 0);

  /**
   * Step completion is DATA-driven, so editing an existing product shows its
   * steps pre-validated. Optional steps (2, 4, 5) also count as complete once
   * the user has moved past them, so the add flow still accumulates checks.
   */
  const stepComplete = (n: number, values: any) => {
    if (n === 1) return !isEmpty(values.productName) && hasValidPrice(values);
    if (n === 2) return photoCount > 0 || visitedMax > 2;
    if (n === 3) return !isEmpty(values.category) && !isEmpty(values.brand);
    if (n === 4)
      return (
        !isEmpty(values.productOptions) ||
        !isEmpty(values.modifiers) ||
        !isEmpty(selectedWarranty) ||
        visitedMax > 4
      );
    if (n === 5)
      return (
        visitedMax > 5 ||
        !isEmpty(values.description) ||
        !!values.showMenu ||
        !!values.sendToKitchen ||
        !!values.sendToBar
      );
    return false;
  };

  const goToStep = (n: number) => {
    setStep(n);
    setVisitedMax((prev) => Math.max(prev, n));
  };

  const canContinue = (values: any) => {
    if (step === 1) return stepComplete(1, values);
    if (step === 3) return stepComplete(3, values);
    return true;
  };

  const footerHint = (values: any) => {
    if (isEmpty(values.productName)) return 'Add a product name to continue';
    if (!hasValidPrice(values)) return 'Add a selling price to continue';
    if (isEmpty(values.category)) return 'Select a category to continue';
    if (isEmpty(values.brand)) return 'Select a brand to continue';
    return null;
  };

  const previewPrice = (values: any) => {
    if (!isEmpty(values.priceVariants)) {
      const prices = values.priceVariants
        .map((v: any) => parseFloat(v.price))
        .filter((p: number) => !isNaN(p) && p > 0);
      if (prices.length > 0) return `from ${formatPrice(Math.min(...prices), selectedShop?.currency)}`;
    }
    const p = parseFloat(values.itemPrice);
    if (!isNaN(p) && p > 0) return formatPrice(p, selectedShop?.currency);
    return 'No price yet';
  };

  const photoCount =
    (imageId ? 1 : 0) + (menuImage ? 1 : 0) + productImages.length;

  // ── Shared small renderers ────────────────────────────────────────────────
  const renderToggleRow = (
    label: string,
    hint: string,
    checked: boolean,
    onChange: (v: boolean) => void
  ) => (
    <div className="flex items-center justify-between py-3.5 px-4 border border-borderc rounded-xl">
      <div>
        <p className="text-sm font-semibold text-text1">{label}</p>
        <p className="text-xs text-text3 mt-0.5">{hint}</p>
      </div>
      <Switch
        width={44}
        height={22}
        onColor="#4E45E4"
        checkedIcon={false}
        handleDiameter={16}
        uncheckedIcon={false}
        checked={!!checked}
        onChange={onChange}
      />
    </div>
  );

  const reviewRow = (
    label: string,
    value: any,
    targetStep: number,
    action = 'Change'
  ) => (
    <div className="flex items-center justify-between py-3 border-b border-borderc last:border-0">
      <div className="min-w-0">
        <p className="text-xs text-text3">{label}</p>
        <p className="text-sm font-medium text-text1 truncate">
          {value || '—'}
        </p>
      </div>
      <button
        type="button"
        onClick={() => goToStep(targetStep)}
        className="text-sm font-medium text-brand hover:text-brand2 flex-shrink-0 ml-4"
      >
        {action}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      style={customStyles}
      contentLabel="Product Modal"
    >
      <div className="bg-surface shadow-pop rounded-2xl w-[min(1100px,94vw)] flex flex-col max-h-[92vh] overflow-hidden">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 px-6 pt-5 pb-4">
          {isSubModal && (
            <button
              type="button"
              onClick={() => onPressSubModalBack()}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-text3 hover:bg-surface2 hover:text-text1 transition-colors"
            >
              <HiOutlineArrowLeft size={18} />
            </button>
          )}
          <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center flex-shrink-0">
            <HiOutlineShoppingBag size={20} className="text-brand" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-text1 leading-tight">
              {isEdit ? 'Edit product' : 'New product'}
            </h3>
            <p className="text-[13px] text-text3">
              {isSubModal
                ? openProductOption
                  ? 'Price options'
                  : openPriceVariant
                  ? 'Price variants'
                  : !isEmpty(editModifier)
                  ? 'Edit modifier'
                  : 'Add modifier'
                : `Step ${step} of 6 · ${STEPS[step - 1].title}`}
              {isUploading ? ' · uploading…' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onCloseModal()}
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-borderc text-text3 hover:bg-surface2 hover:text-text1 transition-colors"
          >
            <AiOutlineClose size={16} />
          </button>
        </div>

        {/* ── Progress bar ───────────────────────────────────────────────── */}
        <div className="h-[3px] bg-surface3 w-full">
          <div
            className="h-full bg-brand transition-all duration-300"
            style={{width: `${(step / 6) * 100}%`}}
          />
        </div>

        <Formik
          validateOnBlur={true}
          validateOnChange={true}
          enableReinitialize={true}
          onSubmit={handleSubmitProduct}
          initialValues={productInitial}
          validationSchema={validationSchema}
          validate={(values) => {
            const errors: any = {};
            if (!isEmpty(values.productOptions)) {
              errors.itemPrice = null;
            }
            return omitBy(errors, isNull);
          }}
        >
          {({errors, values, touched, setErrors, handleSubmit, handleChange, setFieldValue, submitCount}) => {
            const hint = footerHint(values);
            return (
              <form
                id="productForm"
                onSubmit={handleSubmit}
                className="flex flex-col flex-1 min-h-0"
              >
                <div className="flex flex-1 min-h-0">
                  {/* ── Left rail: steps + live preview ─────────────────── */}
                  {!isSubModal && (
                    <div className="w-[250px] flex-shrink-0 border-r border-borderc bg-ghost-white flex flex-col overflow-y-auto">
                      <p className="nl-caps px-5 pt-5 pb-2">Steps</p>
                      <div className="px-3 space-y-0.5">
                        {STEPS.map((s) => {
                          const active = step === s.n;
                          const complete = stepComplete(s.n, values) && s.n !== step;
                          return (
                            <button
                              key={s.n}
                              type="button"
                              onClick={() => goToStep(s.n)}
                              className={`w-full flex items-start gap-3 px-2.5 py-2.5 rounded-xl text-left transition-colors ${
                                active ? 'bg-brand-soft' : 'hover:bg-surface2'
                              }`}
                            >
                              <span
                                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5 ${
                                  active
                                    ? 'bg-brand text-white'
                                    : complete
                                    ? 'bg-pos-soft text-pos'
                                    : 'bg-surface3 text-text3'
                                }`}
                              >
                                {complete ? <HiCheck size={12} /> : s.n}
                              </span>
                              <span className="min-w-0">
                                <span
                                  className={`block text-sm font-semibold leading-tight ${
                                    active ? 'text-brand' : 'text-text1'
                                  }`}
                                >
                                  {s.title}
                                </span>
                                <span className="block text-xs text-text3 mt-0.5">
                                  {s.sub}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Live preview */}
                      <div className="mt-auto border-t border-borderc px-5 py-4">
                        <p className="nl-caps pb-2">Live preview</p>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-surface3 overflow-hidden flex items-center justify-center flex-shrink-0">
                            {imageId ? (
                              <S3Image
                                url={imageId}
                                className="w-10 h-10 object-cover"
                                defaultImage={defaultImage}
                              />
                            ) : isString(menuImage) && menuImage ? (
                              <S3Image
                                type="menu"
                                url={menuImage}
                                className="w-10 h-10 object-cover"
                                defaultImage={defaultImage}
                              />
                            ) : (
                              <span className="text-text3 text-xs">—</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[13px] font-semibold text-text1 truncate">
                              {values.productName || 'Untitled product'}
                            </p>
                            <p className="text-xs text-text3 font-mono">
                              {previewPrice(values)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Body ───────────────────────────────────────────── */}
                  <div className="flex-1 min-w-0 overflow-y-auto">
                    {openProductOption ? (
                      <>
                        <OptionForm
                          product={selectedProduct}
                          productOptions={productInitial.productOptions}
                        />
                        <div className="flex flex-row justify-between items-center px-6 py-4">
                          <button
                            type="button"
                            onClick={() => {
                              if (isEmpty(product)) {
                                setFieldValue(`productOptions`, []);
                              }
                              setOpenProductOption(false);
                            }}
                            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-neg border border-neg-soft bg-neg-soft hover:opacity-80"
                          >
                            Remove All
                          </button>
                          <div
                            onClick={() => {
                              let prevErrors = {
                                ...errors,
                                itemPrice: undefined,
                              };
                              setErrors(prevErrors);
                              if (isEmpty(errors?.productOptions)) {
                                addOptionVariants(values, setFieldValue);
                              }
                            }}
                            className="cursor-pointer px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-brand hover:bg-brand2"
                          >
                            {isEdit ? 'Update Options' : 'Add Price Variations'}
                          </div>
                        </div>
                      </>
                    ) : openPriceVariant ? (
                      <>
                        <PriceForm
                          productOptions={productOptions}
                          variantStock={
                            product?.trackByVariant === true ? product?.variantStock ?? {} : undefined
                          }
                          addProductOption={() => addProductOption(true, values)}
                        />
                        <div className="flex flex-row justify-end items-center px-6 py-4">
                          <div
                            className="cursor-pointer px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-brand hover:bg-brand2"
                            onClick={() => {
                              if (isEmpty(errors?.priceVariants)) {
                                setOpenPriceVariant(false);
                              }
                            }}
                          >
                            {isEdit ? 'Update Variations' : 'Create Variations'}
                          </div>
                        </div>
                      </>
                    ) : openProductModifier ? (
                      <>
                        <ModifierForm
                          product={selectedProduct}
                          editModifier={editModifier}
                          productModifier={(productInitial as any).modifier}
                          removeEditModifier={() => setEditModifier({})}
                        />
                        <div className="flex flex-row justify-between items-center px-6 py-4">
                          <button
                            type="button"
                            onClick={() => {
                              setEditModifier({});
                              if (isEmpty(product)) {
                                const filterModifiers = filter(
                                  values.modifiers,
                                  (modifier: any, index: number) =>
                                    index !== values.modifiers.length - 1
                                );
                                setFieldValue(`modifiers`, [...filterModifiers]);
                              }
                              setOpenProductModifier(false);
                            }}
                            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-neg border border-neg-soft bg-neg-soft hover:opacity-80"
                          >
                            Cancel
                          </button>
                          <div
                            onClick={() => {
                              setEditModifier({});
                              if (isEmpty(errors?.modifiers)) {
                                setOpenProductModifier(false);
                              }
                            }}
                            className="cursor-pointer px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-brand hover:bg-brand2"
                          >
                            {isEdit ? 'Update Modifier' : 'Add Modifier'}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="px-8 py-6">
                        {/* ── Step 1 · Essentials ─────────────────────── */}
                        {step === 1 && (
                          <div className="max-w-2xl">
                            <h2 className="text-xl font-semibold text-text1">
                              The essentials
                            </h2>
                            <p className="text-sm text-text2 mt-1 mb-6">
                              Name and price are all you need to start selling —
                              everything else can wait.
                            </p>

                            <div className="mb-1">
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-sm font-medium text-text1">
                                  Product name <span className="text-neg">*</span>
                                </label>
                                <span className="text-xs text-text3">
                                  {(values.productName || '').length}/70
                                </span>
                              </div>
                              <input
                                id="productName"
                                type="text"
                                maxLength={70}
                                autoComplete="off"
                                value={values.productName}
                                placeholder="e.g. Wooden Rainbow Stacking Ring Tower"
                                onChange={(e) => {
                                  setFieldValue('productName', e.target.value);
                                  if (!slugEditing) {
                                    setFieldValue('slug', kebabCase(e.target.value));
                                  }
                                }}
                                className="w-full px-4 py-3 border border-borderc rounded-xl text-[15px] text-text1 outline-none focus:ring-2 focus:ring-brand bg-surface"
                              />
                              {!isEmpty(errors.productName) &&
                                (touched.productName || submitCount > 0) && (
                                  <p className="text-xs text-neg mt-1">
                                    {String(errors.productName)}
                                  </p>
                                )}
                            </div>

                            {/* Menu link / slug */}
                            <div className="flex items-center gap-2 mb-6 mt-2">
                              <span className="text-[13px] text-text3">Menu link</span>
                              {slugEditing ? (
                                <input
                                  autoFocus
                                  value={values.slug || ''}
                                  onChange={handleChange('slug')}
                                  onBlur={() => setSlugEditing(false)}
                                  className="px-2 py-1 text-[13px] font-mono border border-borderc rounded-lg outline-none focus:ring-2 focus:ring-brand"
                                />
                              ) : (
                                <span className="px-2 py-1 text-[12.5px] font-mono bg-surface2 border border-borderc rounded-lg text-text2">
                                  /{values.slug || 'product-slug'}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => setSlugEditing(!slugEditing)}
                                className="text-[13px] font-medium text-brand hover:text-brand2"
                              >
                                {slugEditing ? 'Done' : 'Edit'}
                              </button>
                            </div>

                            {isEmpty(values.productOptions) ? (
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-medium text-text1 block mb-1">
                                    Selling price
                                  </label>
                                  <div className="flex items-center border border-borderc rounded-xl px-3.5 py-3 focus-within:ring-2 focus-within:ring-brand">
                                    <span className="text-[13px] font-mono text-text3 mr-2">
                                      {selectedShop?.currency || 'LKR'}
                                    </span>
                                    <input
                                      id="price"
                                      type="text"
                                      inputMode="decimal"
                                      value={values.itemPrice}
                                      placeholder="0.00"
                                      onChange={handleChange('itemPrice')}
                                      className="flex-1 text-right font-mono text-[15px] text-text1 outline-none bg-transparent min-w-0"
                                    />
                                  </div>
                                  <p
                                    className={`text-xs mt-1 ${
                                      !hasValidPrice(values) ? 'text-warn' : 'text-text3'
                                    }`}
                                  >
                                    {!hasValidPrice(values)
                                      ? 'Required to sell'
                                      : 'Shown at the till'}
                                  </p>
                                  {!isEmpty(errors.itemPrice) &&
                                    (touched.itemPrice || submitCount > 0) && (
                                      <p className="text-xs text-neg mt-1">
                                        {String(errors.itemPrice)}
                                      </p>
                                    )}
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-text1 block mb-1">
                                    Price before discount
                                  </label>
                                  <div className="flex items-center border border-borderc rounded-xl px-3.5 py-3 focus-within:ring-2 focus-within:ring-brand">
                                    <span className="text-[13px] font-mono text-text3 mr-2">
                                      {selectedShop?.currency || 'LKR'}
                                    </span>
                                    <input
                                      id="actualPrice"
                                      type="text"
                                      inputMode="decimal"
                                      value={values.actualPrice ?? ''}
                                      placeholder="0.00"
                                      onChange={handleChange('actualPrice')}
                                      className="flex-1 text-right font-mono text-[15px] text-text1 outline-none bg-transparent min-w-0"
                                    />
                                  </div>
                                  <p className="text-xs text-text3 mt-1">
                                    Optional · shows a strike-through
                                  </p>
                                </div>

                                {/* Only when the shop has product shipping
                                    switched on, under Settings → Company
                                    Info. Blank uses the shop's base fee; 0
                                    ships free. */}
                                {shipping.enabled && (
                                  <div>
                                    <label className="text-sm font-medium text-text1 block mb-1">
                                      Shipping fee
                                    </label>
                                    <div className="flex items-center border border-borderc rounded-xl px-3.5 py-3 focus-within:ring-2 focus-within:ring-brand">
                                      <span className="text-[13px] font-mono text-text3 mr-2">
                                        {selectedShop?.currency || 'LKR'}
                                      </span>
                                      <input
                                        id="shippingFee"
                                        type="text"
                                        inputMode="decimal"
                                        value={`${values.shippingFee ?? ''}`}
                                        placeholder={
                                          shipping.baseFee
                                            ? `${shipping.baseFee}`
                                            : '0.00'
                                        }
                                        onChange={handleChange('shippingFee')}
                                        className="flex-1 text-right font-mono text-[15px] text-text1 outline-none bg-transparent min-w-0"
                                      />
                                    </div>
                                    <p className="text-xs text-text3 mt-1">
                                      {shipping.baseFee
                                        ? `Blank uses the base fee (${shipping.baseFee}) · 0 ships free`
                                        : 'Blank uses the shop base fee · 0 ships free'}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="p-4 bg-brand-soft border border-borderc rounded-xl flex items-center justify-between">
                                <p className="text-sm text-text2">
                                  Pricing comes from your{' '}
                                  <span className="font-semibold text-text1">
                                    {values.priceVariants?.length ?? 0} price variant
                                    {(values.priceVariants?.length ?? 0) !== 1 ? 's' : ''}
                                  </span>
                                  .
                                </p>
                                <button
                                  type="button"
                                  onClick={() => goToStep(4)}
                                  className="text-sm font-medium text-brand hover:text-brand2"
                                >
                                  Manage options
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ── Step 2 · Photos ─────────────────────────── */}
                        {step === 2 && (
                          <div className="max-w-3xl">
                            <h2 className="text-xl font-semibold text-text1">Photos</h2>
                            <p className="text-sm text-text2 mt-1 mb-6">
                              One clear photo on a plain background sells better than
                              five busy ones.
                            </p>

                            <div className="grid grid-cols-2 gap-5 mb-6">
                              {/* Primary product image */}
                              <div>
                                <Dropzone
                                  multiple={false}
                                  onDrop={(files) => onAddFile(files)}
                                  accept={{'image/png': ['.png', '.jpeg', '.JPG']}}
                                >
                                  {({getRootProps, getInputProps}: any) => (
                                    <div
                                      {...getRootProps()}
                                      className="relative h-56 rounded-2xl border-2 border-dashed border-brand bg-brand-soft flex flex-col items-center justify-center cursor-pointer overflow-hidden hover:opacity-90 transition-opacity"
                                    >
                                      <input {...getInputProps()} />
                                      {imageId ? (
                                        <>
                                          <S3Image
                                            url={imageId}
                                            className="absolute inset-0 w-full h-full object-cover"
                                            defaultImage={defaultImage}
                                          />
                                          <img
                                            alt="remove"
                                            src={ImageRemoveIcon}
                                            onClick={(e) => {
                                              removeImage();
                                              e.stopPropagation();
                                            }}
                                            className="w-6 absolute right-2 top-2 cursor-pointer z-10"
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <HiOutlineArrowUpTray
                                            size={22}
                                            className="text-brand mb-2"
                                          />
                                          <p className="text-sm font-semibold text-brand">
                                            Primary product image
                                          </p>
                                          <p className="text-xs text-text3 mt-1">
                                            Drag &amp; drop, or browse · JPG or PNG, up to 10 MB
                                          </p>
                                        </>
                                      )}
                                      {loadingAction?.loading &&
                                        loadingAction?.action?.type === 'UPLOAD_IMAGE' && (
                                          <div className="plan-loading absolute" />
                                        )}
                                    </div>
                                  )}
                                </Dropzone>
                              </div>

                              {/* Menu image */}
                              <div>
                                <Dropzone
                                  multiple={false}
                                  onDrop={(files) => setImageToUpload(files)}
                                  accept={{'image/png': ['.png', '.jpeg', '.JPG']}}
                                >
                                  {({getRootProps, getInputProps}: any) => (
                                    <div
                                      {...getRootProps()}
                                      className="relative h-56 rounded-2xl border border-dashed border-borderc2 bg-surface2 flex flex-col items-center justify-center cursor-pointer overflow-hidden hover:bg-surface3 transition-colors"
                                    >
                                      <input {...getInputProps()} />
                                      {menuImage ? (
                                        <>
                                          {isString(menuImage) ? (
                                            <S3Image
                                              type="menu"
                                              url={menuImage}
                                              className="absolute inset-0 w-full h-full object-cover"
                                              defaultImage={defaultImage}
                                            />
                                          ) : (
                                            <img
                                              alt="menu"
                                              src={URL.createObjectURL(menuImage)}
                                              className="absolute inset-0 w-full h-full object-cover"
                                            />
                                          )}
                                          <img
                                            alt="remove"
                                            src={ImageRemoveIcon}
                                            onClick={(e) => {
                                              setMenuImage(undefined);
                                              e.stopPropagation();
                                            }}
                                            className="w-6 absolute right-2 top-2 cursor-pointer z-10"
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <HiOutlinePhoto
                                            size={22}
                                            className="text-text3 mb-2"
                                          />
                                          <p className="text-sm font-semibold text-text2">
                                            Menu image
                                          </p>
                                          <p className="text-xs text-text3 mt-1">
                                            Shown in the menu app &amp; storefront
                                          </p>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </Dropzone>
                                <p className="text-xs text-text3 mt-2">
                                  Leave the menu image empty to reuse the primary photo.
                                </p>
                              </div>
                            </div>

                            {/* Gallery */}
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-semibold text-text1">Gallery</p>
                              <p className="text-xs text-text3">Extra angles &amp; detail shots</p>
                            </div>
                            <div className="grid grid-cols-5 gap-3">
                              {productImages.map((productImage, index) => (
                                <div
                                  key={index}
                                  className="relative h-24 rounded-xl overflow-hidden border border-borderc"
                                >
                                  {isString(productImage) ? (
                                    <S3Image
                                      type="menu"
                                      url={productImage}
                                      className="w-full h-full object-cover"
                                      defaultImage={defaultImage}
                                    />
                                  ) : (
                                    <img
                                      alt="gallery"
                                      src={URL.createObjectURL(productImage)}
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                  <img
                                    alt="remove"
                                    src={ImageRemoveIcon}
                                    onClick={() => removeProductImage(index)}
                                    className="w-5 absolute right-1.5 top-1.5 cursor-pointer"
                                  />
                                </div>
                              ))}
                              <Dropzone
                                multiple
                                onDrop={(files) => setProductImagesToUploaded(files)}
                                accept={{'image/png': ['.png', '.jpeg', '.JPG']}}
                              >
                                {({getRootProps, getInputProps}: any) => (
                                  <div
                                    {...getRootProps()}
                                    className="h-24 rounded-xl border border-dashed border-borderc2 bg-surface2 flex flex-col items-center justify-center cursor-pointer hover:bg-surface3 transition-colors"
                                  >
                                    <input {...getInputProps()} />
                                    <HiPlus size={16} className="text-text3" />
                                    <span className="text-xs text-text3 mt-1">
                                      {isEmpty(productImages) ? 'Upload' : 'Add'}
                                    </span>
                                  </div>
                                )}
                              </Dropzone>
                            </div>
                          </div>
                        )}

                        {/* ── Step 3 · Organisation ───────────────────── */}
                        {step === 3 && (
                          <div className="max-w-2xl">
                            <h2 className="text-xl font-semibold text-text1">
                              Where it belongs
                            </h2>
                            <p className="text-sm text-text2 mt-1 mb-6">
                              Grouping keeps reports and the till layout tidy.
                            </p>

                            {/* ── Stock tracking ─────────────────────────
                                Replaces the old free-typed "Stock count".
                                A count is no longer something you type: it
                                is derived from the ledger, so what you set
                                here is only HOW the item is tracked. */}
                            <div className="mb-6 rounded-xl border border-borderc p-4">
                              <div className="flex items-baseline justify-between mb-3">
                                <h3 className="text-sm font-semibold text-text1">
                                  Stock tracking
                                </h3>
                                {isEdit && values.stockMode !== 'none' && (
                                  <span className="text-xs text-text2 font-mono">
                                    {get(product, 'stockOnHand', 0)} on hand
                                    {get(product, 'stockUpdatedAt') && (
                                      <>
                                        {' · '}
                                        {new Date(
                                          get(product, 'stockUpdatedAt')
                                        ).toLocaleString()}
                                      </>
                                    )}
                                  </span>
                                )}
                              </div>

                              <div className="grid grid-cols-3 gap-2 mb-2">
                                {STOCK_MODES.map((m) => (
                                  <button
                                    key={m.value}
                                    type="button"
                                    onClick={() => {
                                      setFieldValue('stockMode', m.value);
                                      // A refunded physical unit goes back on
                                      // the shelf; refunded prepared food does
                                      // not — so the default follows the mode.
                                      setFieldValue(
                                        'returnToStockOnRefund',
                                        m.value === 'unit'
                                      );
                                      if (m.value !== 'unit')
                                        setFieldValue('parLevel', '');
                                    }}
                                    className={`rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                                      values.stockMode === m.value
                                        ? 'border-brand bg-brand-soft text-brand'
                                        : 'border-borderc text-text2 hover:bg-surface2'
                                    }`}
                                  >
                                    {m.label}
                                  </button>
                                ))}
                              </div>
                              <p className="text-xs text-text3 mb-3">
                                {
                                  STOCK_MODES.find(
                                    (m) => m.value === values.stockMode
                                  )?.hint
                                }
                              </p>

                              {values.stockMode === 'recipe' && (
                                <RecipeEditor
                                  recipe={values.recipe ?? []}
                                  ingredients={ingredients ?? []}
                                  onChange={(next: any) =>
                                    setFieldValue('recipe', next)
                                  }
                                />
                              )}

                              {(values.stockMode === 'unit' ||
                                values.stockMode === 'recipe') && (
                                <div className="mb-3">
                                  <FormInput
                                    id="parLevel"
                                    type="number"
                                    label={
                                      values.stockMode === 'recipe'
                                        ? 'Par level (portions)'
                                        : 'Par level'
                                    }
                                    error={errors.parLevel}
                                    value={`${values.parLevel ?? ''}`}
                                    touched={touched.parLevel}
                                    onChange={(val: any) =>
                                      setFieldValue('parLevel', val)
                                    }
                                  />
                                  <p className="text-xs text-text3 mt-1">
                                    Tills warn when the count reaches this
                                  </p>
                                </div>
                              )}

                              {values.stockMode === 'unit' &&
                                !canTrackByVariant('unit', product) &&
                                !isEmpty(values.priceVariants) && (
                                  <p className="mb-3 text-xs text-text3">
                                    Save the product once to track stock per variant.
                                  </p>
                                )}

                              {canTrackByVariant(values.stockMode, product) && (
                                <div className="mb-3 rounded-lg border border-borderc p-3">
                                  <label className="flex items-center justify-between gap-3">
                                    <span>
                                      <span className="block text-sm text-text1">
                                        Track stock per variant
                                      </span>
                                      <span className="block text-xs text-text3">
                                        Each variant keeps its own count; the product shows the total
                                      </span>
                                    </span>
                                    <Switch
                                      onChange={(checked) => setFieldValue('trackByVariant', checked)}
                                      checked={Boolean(values.trackByVariant)}
                                      onColor="#6366F1"
                                      checkedIcon={false}
                                      uncheckedIcon={false}
                                      height={20}
                                      width={38}
                                    />
                                  </label>
                                  {values.trackByVariant && (
                                    <VariantStockTable
                                      rows={variantRows(product).map((r) =>
                                        variantOnHand[r.variantId] != null
                                          ? {...r, onHand: variantOnHand[r.variantId]}
                                          : r,
                                      )}
                                      pars={values.variantConfig ?? {}}
                                      productPar={values.parLevel}
                                      canAdjust={product?.trackByVariant === true}
                                      onParChange={(variantId, value) =>
                                        setFieldValue('variantConfig', {
                                          ...(values.variantConfig ?? {}),
                                          [variantId]: {parLevel: value},
                                        })
                                      }
                                      onAdjust={(row, mode) => setVariantAdjust({row, mode})}
                                    />
                                  )}
                                  {variantNotice && (
                                    <p className="mt-2 text-xs text-emerald-700">{variantNotice}</p>
                                  )}
                                </div>
                              )}

                              {values.stockMode !== 'none' && (
                                <label className="flex items-center justify-between gap-3">
                                  <span>
                                    <span className="block text-sm text-text1">
                                      Return to stock on refund
                                    </span>
                                    <span className="block text-xs text-text3">
                                      Put the units back when an order is refunded
                                    </span>
                                  </span>
                                  <Switch
                                    onChange={(checked) =>
                                      setFieldValue('returnToStockOnRefund', checked)
                                    }
                                    checked={Boolean(values.returnToStockOnRefund)}
                                    onColor="#6366F1"
                                    checkedIcon={false}
                                    uncheckedIcon={false}
                                    height={20}
                                    width={38}
                                  />
                                </label>
                              )}

                              {isEdit && values.stockMode !== 'none' && (
                                <p className="text-xs text-text3 mt-3 pt-3 border-t border-borderc">
                                  Counts come from the till ledger. Adjust them
                                  on the Stock&nbsp;Levels screen, not here.
                                </p>
                              )}
                            </div>

                            <div className="mb-5">
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-sm font-medium text-text1">
                                  Category <span className="text-neg">*</span>
                                </label>
                                <button
                                  type="button"
                                  onClick={() => toggleCategoryModal()}
                                  className="text-[13px] font-medium text-brand hover:text-brand2"
                                >
                                  New category
                                </button>
                              </div>
                              <Field
                                id="category"
                                name="category"
                                isMulti={false}
                                options={categoryList}
                                error={errors.category}
                                component={CustomSelect}
                                touched={touched.category}
                                placeholder="Select category"
                              />
                            </div>

                            <div className="mb-5">
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-sm font-medium text-text1">
                                  Brand <span className="text-neg">*</span>
                                </label>
                                <button
                                  type="button"
                                  onClick={() => toggleBrandModal()}
                                  className="text-[13px] font-medium text-brand hover:text-brand2"
                                >
                                  New brand
                                </button>
                              </div>
                              <Field
                                id="brand"
                                name="brand"
                                isMulti={false}
                                options={brandList}
                                error={errors.brand}
                                touched={touched.brand}
                                component={CustomSelect}
                                placeholder="Select brand"
                              />
                            </div>

                            <div className="mb-5">
                              <label className="text-sm font-medium text-text1 block mb-1">
                                Tags
                              </label>
                              <CreatableSelect
                                isMulti
                                isClearable
                                {...portalMenu(selectStyles)}
                                options={
                                  tags.map(
                                    ({tagName, tagId, shopId}: ProductTag) => ({
                                      value: tagId,
                                      label: tagName,
                                      shopId,
                                    })
                                  ) as any
                                }
                                value={values.tags}
                                placeholder="Select or create new tags"
                                onCreateOption={(value: string) =>
                                  createProductTag({
                                    shopId: selectedShop.shopId,
                                    tagId: nanoid(8),
                                    tagName: value,
                                  })
                                }
                                onChange={(option) => setFieldValue('tags', option)}
                              />
                            </div>
                          </div>
                        )}

                        {/* ── Step 4 · Options ────────────────────────── */}
                        {step === 4 && (
                          <div className="max-w-2xl">
                            <h2 className="text-xl font-semibold text-text1">
                              Variants &amp; add-ons
                            </h2>
                            <p className="text-sm text-text2 mt-1 mb-6">
                              Add these only if staff need choices at the till.
                            </p>

                            {/* Price options */}
                            <div className="border border-borderc rounded-xl p-4 mb-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-semibold text-text1">
                                    Price options
                                  </p>
                                  <p className="text-xs text-text3 mt-0.5">
                                    Differentiate price by variant — Large, Medium and
                                    Small.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => addProductOption(true, values)}
                                  className="px-3 py-2 rounded-lg text-[13px] font-semibold text-brand bg-brand-soft hover:opacity-80 flex-shrink-0"
                                >
                                  {isEmpty(values.productOptions)
                                    ? 'Add option'
                                    : 'Edit options'}
                                </button>
                              </div>
                              {!isEmpty(values.priceVariants) && (
                                <div
                                  onClick={() => setOpenPriceVariant(true)}
                                  className="mt-3 border-t border-borderc pt-3 cursor-pointer"
                                >
                                  {values.priceVariants.map(
                                    (variant: any, index: number) => (
                                      <div
                                        key={index}
                                        className="flex justify-between py-1.5 text-sm"
                                      >
                                        <span className="text-text2">
                                          {variant.optionUnit}
                                        </span>
                                        <span className="font-mono font-medium text-text1">
                                          {formatPrice(variant.price, selectedShop?.currency)}
                                        </span>
                                      </div>
                                    )
                                  )}
                                  <p className="text-xs text-brand mt-1.5">
                                    Tap to edit variant prices
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Modifier sets */}
                            <div className="border border-borderc rounded-xl p-4 mb-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-semibold text-text1">
                                    Modifier sets
                                  </p>
                                  <p className="text-xs text-text3 mt-0.5">
                                    Let staff customise the item at the till, such as
                                    extras or swaps.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => addProductModifier(values, errors)}
                                  className="px-3 py-2 rounded-lg text-[13px] font-semibold text-brand bg-brand-soft hover:opacity-80 flex-shrink-0"
                                >
                                  Add modifier
                                </button>
                              </div>
                              {!isEmpty(values.modifiers) && (
                                <div className="mt-3 border-t border-borderc pt-2">
                                  {values.modifiers.map(
                                    (modifier: any, index: number) => (
                                      <div
                                        key={modifier.id ?? index}
                                        className="flex items-center justify-between py-2"
                                      >
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setEditModifier(modifier);
                                            setOpenProductModifier(true);
                                          }}
                                          className="text-sm font-medium text-text1 hover:text-brand text-left"
                                        >
                                          {modifier.title || 'Untitled modifier'}
                                          <span className="text-xs text-text3 ml-2">
                                            {modifier.items?.length ?? 0} item
                                            {(modifier.items?.length ?? 0) !== 1
                                              ? 's'
                                              : ''}
                                          </span>
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setFieldValue(
                                              'modifiers',
                                              values.modifiers.filter(
                                                (_: any, i: number) => i !== index
                                              )
                                            )
                                          }
                                          className="text-xs font-medium text-neg hover:opacity-70"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Warranty — RETAILS shops only (unchanged rule) */}
                            {selectedShop?.shopCategory === 'RETAILS' && (
                              <div className="border border-borderc rounded-xl p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-sm font-semibold text-text1">
                                    Warranty
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => toggleWarrantyModal()}
                                    className="text-[13px] font-medium text-brand hover:text-brand2"
                                  >
                                    New warranty
                                  </button>
                                </div>
                                <Select
                                  {...portalMenu(selectStyles)}
                                  options={warrantyList}
                                  placeholder="No warranty"
                                  value={
                                    !isEmpty(selectedWarranty)
                                      ? {
                                          value: selectedWarranty.wrid,
                                          label: capitalize(selectedWarranty.title),
                                        }
                                      : null
                                  }
                                  onChange={(option: any) => {
                                    const result = find(
                                      warranties,
                                      (w: any) => w.wrid === option?.value
                                    );
                                    setSelectedWarranty(result || {});
                                  }}
                                />
                                <p className="text-xs text-text3 mt-2">
                                  Printed on the customer bill when set.
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ── Step 5 · Behaviour ──────────────────────── */}
                        {step === 5 && (
                          <div className="max-w-2xl">
                            <h2 className="text-xl font-semibold text-text1">
                              How it behaves
                            </h2>
                            <p className="text-sm text-text2 mt-1 mb-6">
                              Routing and the description shown on the menu and
                              receipts.
                            </p>

                            <div className="space-y-3 mb-6">
                              {renderToggleRow(
                                'Display in menu app',
                                'Customers can see and order this item.',
                                values.showMenu,
                                (v) => setFieldValue('showMenu', v)
                              )}
                              {renderToggleRow(
                                'Send to kitchen (KOT)',
                                'Print a ticket at the kitchen station.',
                                values.sendToKitchen,
                                (v) => setFieldValue('sendToKitchen', v)
                              )}
                              {renderToggleRow(
                                'Send to bar (BOT)',
                                'Print a ticket at the bar station.',
                                values.sendToBar,
                                (v) => setFieldValue('sendToBar', v)
                              )}
                            </div>

                            <MDXEditorInput
                              ref={mdxRef}
                              id="description"
                              label="Description"
                              error={errors.description}
                              value={values.description}
                              touched={touched.description}
                              onChange={handleChange('description')}
                            />
                          </div>
                        )}

                        {/* ── Step 6 · Review ─────────────────────────── */}
                        {step === 6 && (
                          <div className="max-w-2xl">
                            <h2 className="text-xl font-semibold text-text1">
                              {isEdit ? 'Ready to save' : 'Ready to publish'}
                            </h2>
                            <p className="text-sm text-text2 mt-1 mb-6">
                              A quick look before it goes live at the till.
                            </p>

                            <div className="border border-borderc rounded-xl px-4 mb-5">
                              {reviewRow('Name', values.productName, 1)}
                              {reviewRow(
                                'Selling price',
                                previewPrice(values),
                                isEmpty(values.productOptions) ? 1 : 4
                              )}
                              {reviewRow(
                                'Photos',
                                photoCount > 0
                                  ? `${photoCount} image${photoCount !== 1 ? 's' : ''}`
                                  : 'None uploaded',
                                2,
                                photoCount > 0 ? 'Change' : 'Add'
                              )}
                              {reviewRow(
                                'Stock',
                                (values.stockCount ?? 0) > 0
                                  ? `${values.stockCount} units`
                                  : 'Not tracked',
                                3
                              )}
                              {reviewRow(
                                'Category',
                                get(
                                  find(
                                    categoryList,
                                    (c: any) => c.value === values.category
                                  ),
                                  'label',
                                  ''
                                ),
                                3
                              )}
                              {reviewRow(
                                'Tags',
                                isArray(values.tags) && values.tags.length > 0
                                  ? values.tags.map((t: any) => t.label).join(', ')
                                  : 'None',
                                3
                              )}
                            </div>

                            {/* Product-list preview */}
                            <div className="border border-borderc rounded-xl p-4 flex items-center gap-3 bg-surface2">
                              <div className="w-11 h-11 rounded-lg bg-brand-soft overflow-hidden flex items-center justify-center flex-shrink-0">
                                {imageId ? (
                                  <S3Image
                                    url={imageId}
                                    className="w-11 h-11 object-cover"
                                    defaultImage={defaultImage}
                                  />
                                ) : (
                                  <span className="text-brand text-xs font-bold">
                                    {(values.productName || '??')
                                      .split(' ')
                                      .map((w: string) => w.charAt(0))
                                      .join('')
                                      .slice(0, 2)
                                      .toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-text1 truncate">
                                  {values.productName || 'Untitled product'}
                                </p>
                                <p className="text-xs text-text3">
                                  How it appears in the product list
                                </p>
                              </div>
                              <p className="text-sm font-mono font-semibold text-text1">
                                {previewPrice(values)}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Footer ─────────────────────────────────────────────── */}
                {!isSubModal && (
                  <div className="flex items-center gap-3 px-6 py-4 border-t border-borderc bg-surface flex-shrink-0">
                    {isEdit && !isEmpty(product) ? (
                      <button
                        type="button"
                        onClick={() => setIsOpenDelete(true)}
                        className="px-4 py-2.5 rounded-xl text-sm font-semibold text-neg border border-borderc hover:bg-neg-soft transition-colors"
                      >
                        Delete product
                      </button>
                    ) : null}

                    <div className="flex items-center gap-2 text-[13px] flex-1 min-w-0">
                      {hint ? (
                        <>
                          <HiOutlineExclamationTriangle
                            size={16}
                            className="text-warn flex-shrink-0"
                          />
                          <span className="text-warn truncate">{hint}</span>
                        </>
                      ) : (
                        <>
                          <HiCheck size={16} className="text-pos flex-shrink-0" />
                          <span className="text-text3 truncate">
                            All required details are set
                          </span>
                        </>
                      )}
                    </div>

                    {step > 1 && (
                      <button
                        type="button"
                        onClick={() => goToStep(step - 1)}
                        className="px-5 py-2.5 rounded-xl text-sm font-semibold text-text1 border border-borderc hover:bg-surface2 transition-colors"
                      >
                        Back
                      </button>
                    )}
                    {step < 6 ? (
                      <button
                        type="button"
                        disabled={!canContinue(values)}
                        onClick={() => goToStep(step + 1)}
                        className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-brand hover:bg-brand2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                      >
                        Continue
                        <HiChevronRight size={15} />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={isUploading}
                        className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-brand hover:bg-brand2 disabled:opacity-40 transition-colors"
                      >
                        {isUploading
                          ? 'Uploading…'
                          : isEdit
                          ? 'Update Product'
                          : 'Add Product'}
                      </button>
                    )}
                  </div>
                )}
              </form>
            );
          }}
        </Formik>

        <AddBrand isOpen={isOpenBrand} closeModal={() => toggleBrandModal()} />
        <AddCategory
          isOpen={isOpenCategory}
          closeModal={() => toggleCategoryModal()}
        />
        <AddWarranty
          warranty={{}}
          isOpen={openWarrantyModal}
          closeModal={() => toggleWarrantyModal()}
        />
        {isOpenDelete && (
          <DeleteModal
            closeModal={() => setIsOpenDelete(false)}
            onProductDelete={() => onProductDelete()}
          />
        )}
        {variantAdjust && product && (
          <StockAdjustModal
            shopId={selectedShop?.shopId}
            productId={product.pid}
            variantId={variantAdjust.row.variantId}
            variantLabel={variantAdjust.row.label}
            productName={product.productName}
            onHand={variantOnHand[variantAdjust.row.variantId] ?? variantAdjust.row.onHand ?? 0}
            mode={variantAdjust.mode}
            onClose={() => setVariantAdjust(null)}
            onDone={(message, detail) => {
              const {variantId, onHand} = variantAdjust.row;
              if (detail) {
                setVariantOnHand((m) => ({
                  ...m,
                  [variantId]: afterAdjust(m[variantId] ?? onHand, detail.mode, detail.quantity),
                }));
              }
              setVariantNotice(message);
              setVariantAdjust(null);
            }}
          />
        )}
      </div>
    </Modal>
  );
};
export default connect(
  (state: any) => ({
    tags: state.product.get('tags'),
    brands: state.product.get('brands'),
    imageId: state.product.get('imageId'),
    menuImage: state.product.get('menuImage'),
    categories: state.product.get('categories'),
    warranties: state.warranty.get('warranties'),
    currentUser: state.setting.get('currentUser'),
    loadingAction: state.common.get('loadingAction'),
    selectedShop: state.dashboard.get('selectedShop'),
    // The ingredient master, for the recipe editor.
    ingredients: state.stock.get('ingredients'),
  }),
  {
    fetchIngredients: Actions.stock.fetchIngredients,
    setImage: Actions.product.setImage,
    uploadImage: Actions.product.uploadImage,
    removeImage: Actions.product.removeImage,
    createProduct: Actions.product.createProduct,
    updateProduct: Actions.product.updateProduct,
    deleteProduct: Actions.product.deleteProduct,
    createProductTag: Actions.product.createProductTag,
    fetchProductOptionSets: Actions.product.fetchProductOptionSets,
    fetchProductTags: Actions.product.fetchProductTags,
  }
)(AddProduct);
